import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import Checkbox from "../../../shared/ui/Field/Checkbox";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isAdminMembership,
  isActiveMembership,
  memberDisplayName,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";

import {
  archiveCompanyClient,
  assignCompanyClient,
  createCompanyClientCommunication,
  createCompanyClientContact,
  deleteCompanyClientContact,
  fetchCompanyClient,
  fetchCompanyClientCommunications,
  fetchCompanyClientGrants,
  patchCompanyClient,
  putCompanyClientPreferences,
  revokeCompanyClientGrant,
  updateCompanyClientContact,
  upsertCompanyClientGrant,
} from "../api/companyClients";
import {
  buildCorporatePatchBody,
  clientDisplayName,
  emptyFormFromClient,
  hasConfidentialKey,
  isCorporateClientsAvailable,
  statusLabel,
  typeLabel,
  userDisplayName,
} from "../lib/clientLabels";
import {
  buildCorporateNeedRows,
  buildNeedPayload,
  COMMUNICATION_DIRECTION_OPTIONS,
  COMMUNICATION_TYPE_OPTIONS,
  CONTACT_TYPE_OPTIONS,
  contactTypeLabel,
  communicationDirectionLabel,
  communicationTypeLabel,
  DEAL_TYPE_OPTIONS,
  EMPTY_NEED_DRAFT,
  formatCommunicationTimestamp,
  needDraftFromPreferences,
  PROPERTY_TYPE_OPTIONS,
} from "../lib/crmLabels";

const TYPE_OPTIONS = [
  { value: "buyer", label: "Покупець" },
  { value: "seller", label: "Продавець" },
  { value: "both", label: "Покупець і продавець" },
];

const STATUS_OPTIONS = [
  { value: "lead", label: "Лід" },
  { value: "active", label: "Активний" },
  { value: "inactive", label: "Неактивний" },
  { value: "closed", label: "Закритий" },
];

const NEED_EDITABLE_KEYS = [
  "deal_type",
  "property_type",
  "min_price",
  "max_price",
  "min_area",
  "max_area",
  "rooms_min",
  "rooms_max",
  "city",
  "district",
];

const CORE_EDITABLE_KEYS = [
  "first_name",
  "last_name",
  "phone",
  "email",
  "type",
  "status",
  "source",
  "notes",
];

const CONTACT_EDITABLE_KEYS = ["type", "value", "is_primary"];

const COMM_EDITABLE_KEYS = ["type", "direction", "content"];

const EMPTY_CONTACT_DRAFT = {
  type: "phone",
  value: "",
  is_primary: false,
};

const EMPTY_COMM_DRAFT = {
  type: "call",
  direction: "out",
  content: "",
};

function CompanyClientDetailContent() {
  const { companyId, clientId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [client, setClient] = useState(null);
  const [grants, setGrants] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [communicationsState, setCommunicationsState] = useState("idle");
  const communicationsStateRef = useRef("idle");
  communicationsStateRef.current = communicationsState;
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const formDirtyRef = useRef(false);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  const [mutationBusy, setMutationBusy] = useState(false);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [assignUserId, setAssignUserId] = useState("");
  const [grantDraft, setGrantDraft] = useState({
    user_id: "",
    can_edit: false,
    include_confidential: false,
    expires_at: "",
  });
  const [needEditing, setNeedEditing] = useState(false);
  const [needDraft, setNeedDraft] = useState({ ...EMPTY_NEED_DRAFT });
  const [needFieldErrors, setNeedFieldErrors] = useState({});
  const [coreFieldErrors, setCoreFieldErrors] = useState({});
  const [contactMode, setContactMode] = useState(null);
  const [contactDraft, setContactDraft] = useState({ ...EMPTY_CONTACT_DRAFT });
  const [contactFieldErrors, setContactFieldErrors] = useState({});
  const [commOpen, setCommOpen] = useState(false);
  const [commDraft, setCommDraft] = useState({ ...EMPTY_COMM_DRAFT });
  const [commFieldErrors, setCommFieldErrors] = useState({});

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const clientMatches =
    client != null &&
    clientId != null &&
    String(client.id) === String(clientId);
  const authorityMatches = companyMatches && actorMatches;
  const available =
    authorityMatches && isCorporateClientsAvailable(company);
  const canManage =
    authorityMatches && isAdminMembership(company, membership);
  const canEdit = Boolean(clientMatches && client?.can_edit);
  const canMutateConfidential = Boolean(
    canEdit &&
      (hasConfidentialKey(client, "contacts") ||
        hasConfidentialKey(client, "preferences")),
  );

  const clearProtected = () => {
    setClient(null);
    setGrants([]);
    setAssignees([]);
    setCommunications([]);
    setCommunicationsState("idle");
    setForm(null);
    formDirtyRef.current = false;
    setEditing(false);
    setActionError(null);
    setAssignUserId("");
    setGrantDraft({
      user_id: "",
      can_edit: false,
      include_confidential: false,
      expires_at: "",
    });
    setNeedEditing(false);
    setNeedDraft({ ...EMPTY_NEED_DRAFT });
    setNeedFieldErrors({});
    setCoreFieldErrors({});
    setContactMode(null);
    setContactDraft({ ...EMPTY_CONTACT_DRAFT });
    setContactFieldErrors({});
    setCommOpen(false);
    setCommDraft({ ...EMPTY_COMM_DRAFT });
    setCommFieldErrors({});
  };

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId || !clientId) {
      setState("not-found");
      return;
    }
    const keepVisible =
      background &&
      (stateRef.current === "ready" || stateRef.current === "unavailable");
    const op = scopeGate.beginRead({
      companyId,
      actorId,
      resourceId: clientId,
    });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const mineMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId)
          ? mine.membership
          : null;
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);

      if (!isActiveMembership(companyData, mineMatches)) {
        clearProtected();
        setState("denied");
        return;
      }
      if (!isCorporateClientsAvailable(companyData)) {
        clearProtected();
        setState("unavailable");
        return;
      }

      const detail = await fetchCompanyClient(companyId, clientId);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setClient(detail);
      if (!(keepVisible && formDirtyRef.current)) {
        setForm(emptyFormFromClient(detail));
        setAssignUserId(
          detail.responsible_user?.id != null
            ? String(detail.responsible_user.id)
            : "",
        );
        formDirtyRef.current = false;
      }

      const canReadConfidential =
        hasConfidentialKey(detail, "contacts") ||
        hasConfidentialKey(detail, "preferences");
      if (canReadConfidential) {
        if (!(keepVisible && communicationsStateRef.current === "ready")) {
          setCommunicationsState("loading");
        }
        try {
          const commList = await fetchCompanyClientCommunications(
            companyId,
            clientId,
          );
          if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
            return;
          }
          setCommunications(Array.isArray(commList) ? commList : []);
          setCommunicationsState("ready");
        } catch (commErr) {
          if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
            return;
          }
          const commStatus = commErr?.response?.status;
          // Nested communications 403/404 during the same protected detail load
          // means authority/concealment invalidation — not a valid empty history.
          // Fail closed: clear already-fetched confidential detail; do not stay ready.
          if (commStatus === 403 || commStatus === 404) {
            clearProtected();
            setState(commStatus === 404 ? "not-found" : "denied");
            return;
          }
          setCommunications([]);
          setCommunicationsState("error");
        }
      } else {
        setCommunications([]);
        setCommunicationsState("idle");
      }

      const manage = isAdminMembership(companyData, mineMatches);
      if (manage) {
        const [grantList, roster] = await Promise.all([
          fetchCompanyClientGrants(companyId, clientId),
          fetchMembers(companyId, "active"),
        ]);
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        setGrants(Array.isArray(grantList) ? grantList : []);
        setAssignees(
          (Array.isArray(roster) ? roster : []).filter(
            (m) =>
              m.status === "active" &&
              (m.role === "member" || m.role === "admin"),
          ),
        );
      } else {
        setGrants([]);
        setAssignees([]);
      }
      revalidationConfirmed = true;
      setActionError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
      setState("ready");
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 403 && status !== 404) {
        setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      clearProtected();
      if (status === 404) setState("not-found");
      else if (status === 403) setState("denied");
      else {
        setError(messageFromError(err));
        setState("error");
      }
    } finally {
      settleRevalidationWrites({
        keepVisible: keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [actorId, clientId, companyId, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    busyOpRef.current = null;
    clearProtected();
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [companyId, actorId, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready" || state === "unavailable",
    busy: mutationBusy,
    onRevalidate: () => load({ background: true }),
  });

  const runMutation = async (work, applyResult, fieldErrorConfig) => {
    if (busyOpRef.current || writesSuspendedRef.current) return;
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: clientId,
    });
    busyOpRef.current = op;
    setMutationBusy(true);
    setActionError(null);
    try {
      const result = await work();
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (typeof applyResult === "function") {
        applyResult(result);
      }
      await load();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        clearProtected();
        setState(status === 404 ? "not-found" : "denied");
        return;
      }
      if (status === 400) {
        const keys = fieldErrorConfig?.keys || NEED_EDITABLE_KEYS;
        const parsed = parseDrfFieldErrors(err, keys);
        if (typeof fieldErrorConfig?.setErrors === "function") {
          fieldErrorConfig.setErrors(parsed);
        } else if (Object.keys(parsed).length > 0) {
          setNeedFieldErrors(parsed);
        }
      }
      setActionError(messageFromError(err));
    } finally {
      if (busyOpRef.current === op) {
        busyOpRef.current = null;
        setMutationBusy(false);
      }
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!canEdit || !clientMatches || !form) return;
    const body = buildCorporatePatchBody(client, form);
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    await runMutation(
      () => patchCompanyClient(companyId, clientId, body),
      (updated) => {
        setClient(updated);
        setForm(emptyFormFromClient(updated));
        setEditing(false);
        setCoreFieldErrors({});
      },
      { keys: CORE_EDITABLE_KEYS, setErrors: setCoreFieldErrors },
    );
  };

  const handleArchive = async () => {
    if (!canEdit) return;
    const ok = window.confirm(
      "Архівувати клієнта компанії? Статус стане «Закритий». Видалення недоступне.",
    );
    if (!ok) return;
    await runMutation(() => archiveCompanyClient(companyId, clientId));
  };

  const handleAssign = async () => {
    if (!canManage) return;
    const userId = assignUserId ? Number(assignUserId) : null;
    await runMutation(() =>
      assignCompanyClient(companyId, clientId, userId),
    );
  };

  const handleClearResponsible = async () => {
    if (!canManage) return;
    await runMutation(() =>
      assignCompanyClient(companyId, clientId, null),
    );
  };

  const handleUpsertGrant = async (event) => {
    event.preventDefault();
    if (!canManage || !grantDraft.user_id) return;
    const body = {
      user_id: Number(grantDraft.user_id),
      can_edit: Boolean(grantDraft.can_edit),
      include_confidential: Boolean(grantDraft.include_confidential),
    };
    if (grantDraft.expires_at) {
      body.expires_at = grantDraft.expires_at;
    }
    await runMutation(
      () => upsertCompanyClientGrant(companyId, clientId, body),
      () => {
        setGrantDraft({
          user_id: "",
          can_edit: false,
          include_confidential: false,
          expires_at: "",
        });
      },
    );
  };

  const handleRevokeGrant = async (grantId) => {
    if (!canManage) return;
    await runMutation(() =>
      revokeCompanyClientGrant(companyId, clientId, grantId),
    );
  };

  const setFormField = (key) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    formDirtyRef.current = true;
    setForm((prev) => ({ ...prev, [key]: value }));
    setCoreFieldErrors((prev) => {
      if (!prev[key] && !prev._form) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const setNeedField = (key) => (event) => {
    const value = event.target.value;
    setNeedDraft((prev) => ({ ...prev, [key]: value }));
    setNeedFieldErrors((prev) => {
      if (!prev[key] && !prev._form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next._form;
      return next;
    });
  };

  const openNeedEditor = () => {
    setNeedDraft(needDraftFromPreferences(client?.preferences));
    setNeedFieldErrors({});
    setNeedEditing(true);
  };

  const cancelNeedEditor = () => {
    setNeedDraft(needDraftFromPreferences(client?.preferences));
    setNeedFieldErrors({});
    setNeedEditing(false);
  };

  const handleNeedSave = async (event) => {
    event.preventDefault();
    if (!canMutateConfidential) return;
    setNeedFieldErrors({});
    await runMutation(
      () =>
        putCompanyClientPreferences(
          companyId,
          clientId,
          buildNeedPayload(needDraft),
        ),
      () => {
        setNeedEditing(false);
        setNeedFieldErrors({});
      },
      { keys: NEED_EDITABLE_KEYS, setErrors: setNeedFieldErrors },
    );
  };

  const openContactAdd = () => {
    setContactMode("add");
    setContactDraft({ ...EMPTY_CONTACT_DRAFT });
    setContactFieldErrors({});
  };

  const openContactEdit = (contact) => {
    setContactMode({ id: contact.id });
    setContactDraft({
      type: contact.type || "phone",
      value: contact.value || "",
      is_primary: Boolean(contact.is_primary),
    });
    setContactFieldErrors({});
  };

  const cancelContactEditor = () => {
    setContactMode(null);
    setContactDraft({ ...EMPTY_CONTACT_DRAFT });
    setContactFieldErrors({});
  };

  const setContactField = (key) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setContactDraft((prev) => ({ ...prev, [key]: value }));
    setContactFieldErrors((prev) => {
      if (!prev[key] && !prev._form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next._form;
      return next;
    });
  };

  const handleContactSave = async (event) => {
    event.preventDefault();
    if (!canMutateConfidential || !contactMode) return;
    setContactFieldErrors({});
    const body = {
      type: contactDraft.type,
      value: contactDraft.value,
      is_primary: Boolean(contactDraft.is_primary),
    };
    if (contactMode === "add") {
      await runMutation(
        () => createCompanyClientContact(companyId, clientId, body),
        () => {
          cancelContactEditor();
        },
        { keys: CONTACT_EDITABLE_KEYS, setErrors: setContactFieldErrors },
      );
      return;
    }
    const contactId = contactMode?.id;
    if (contactId == null) return;
    await runMutation(
      () =>
        updateCompanyClientContact(companyId, clientId, contactId, body),
      () => {
        cancelContactEditor();
      },
      { keys: CONTACT_EDITABLE_KEYS, setErrors: setContactFieldErrors },
    );
  };

  const handleContactDelete = async (contactId) => {
    if (!canMutateConfidential) return;
    const ok = window.confirm("Видалити цей контакт?");
    if (!ok) return;
    await runMutation(() =>
      deleteCompanyClientContact(companyId, clientId, contactId),
    );
  };

  const openCommEditor = () => {
    setCommDraft({ ...EMPTY_COMM_DRAFT });
    setCommFieldErrors({});
    setCommOpen(true);
  };

  const cancelCommEditor = () => {
    setCommOpen(false);
    setCommDraft({ ...EMPTY_COMM_DRAFT });
    setCommFieldErrors({});
  };

  const setCommField = (key) => (event) => {
    const value = event.target.value;
    setCommDraft((prev) => ({ ...prev, [key]: value }));
    setCommFieldErrors((prev) => {
      if (!prev[key] && !prev._form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next._form;
      return next;
    });
  };

  const handleCommSave = async (event) => {
    event.preventDefault();
    if (!canMutateConfidential) return;
    setCommFieldErrors({});
    await runMutation(
      () =>
        createCompanyClientCommunication(companyId, clientId, {
          type: commDraft.type,
          direction: commDraft.direction,
          content: commDraft.content ?? "",
        }),
      () => {
        cancelCommEditor();
      },
      { keys: COMM_EDITABLE_KEYS, setErrors: setCommFieldErrors },
    );
  };

  const assigneeOptions = [
    { value: "", label: "Без відповідального" },
    ...assignees.map((m) => ({
      value: String(m.user?.id || ""),
      label: memberDisplayName(m),
    })),
  ];

  const grantUserOptions = [
    { value: "", label: "Обрати учасника" },
    ...assignees.map((m) => ({
      value: String(m.user?.id || ""),
      label: memberDisplayName(m),
    })),
  ];

  const contacts = Array.isArray(client?.contacts) ? client.contacts : [];
  const needRows = buildCorporateNeedRows(client?.preferences);
  const hasPrefs = client?.preferences != null;
  const showAddNeed =
    canMutateConfidential && (!hasPrefs || needRows.length === 0);
  const showEditNeed =
    canMutateConfidential && hasPrefs && needRows.length > 0;
  const showContactsSection = hasConfidentialKey(client, "contacts");
  const showNeedSection = hasConfidentialKey(client, "preferences");
  const showCommunicationsSection =
    hasConfidentialKey(client, "contacts") ||
    hasConfidentialKey(client, "preferences");

  return (
    <div
      className="client-detail-page feature-company-workspace feature-company-clients"
      id="Csection"
    >
      <div className="container">
        <CompanyPageHeader
          title={
            authorityMatches && clientMatches
              ? clientDisplayName(client)
              : "Клієнт компанії"
          }
          subtitle="Корпоративний запис"
          company={authorityMatches ? company : null}
          membership={authorityMatches ? membership : null}
          companyId={companyId}
          activeNav="clients"
          actions={
            <>
              <Link
                to={`/companies/${companyId}/clients`}
                className="action-btn action-btn--ghost"
              >
                До списку
              </Link>
              {available ? (
                <>
                  <Link
                    to={`/companies/${companyId}/tasks?kind=task&client=${clientId}&create=task`}
                    className="btn-more"
                  >
                    Нова задача
                  </Link>
                  <Link
                    to={`/companies/${companyId}/tasks?kind=viewing&client=${clientId}&create=viewing`}
                    className="action-btn"
                  >
                    Новий перегляд
                  </Link>
                </>
              ) : null}
            </>
          }
        />

        {state === "loading" ? (
          <div className="feature-company-workspace__state">
            <Spinner />
          </div>
        ) : null}
        {state === "unavailable" ? (
          <EmptyState
            title="Корпоративні клієнти недоступні"
            description="Деталі клієнта не завантажуються."
          />
        ) : null}
        {state === "denied" ? (
          <EmptyState
            title="Немає доступу"
            description="Недостатньо прав або членство неактивне."
          />
        ) : null}
        {state === "not-found" ? (
          <EmptyState
            title="Не знайдено"
            description="Запис недоступний."
          />
        ) : null}
        {state === "error" ? (
          <EmptyState
            title="Помилка"
            description={error || "Спробуйте ще раз."}
            primaryAction={
              <button type="button" className="action-btn" onClick={load}>
                Повторити
              </button>
            }
          />
        ) : null}

        {state === "ready" && available && clientMatches ? (
          <div className="cd-grid">
            <div className="cd-main">
              {actionError ? (
                <p className="feature-company-workspace__error" role="alert">
                  {actionError}
                </p>
              ) : null}

              <section className="feature-company-workspace__card">
                <h2>Основні дані</h2>
                <div className="cd-header__pills">
                  <span className={`type-pill type-pill--${client.type}`}>
                    {typeLabel(client.type)}
                  </span>
                  <span
                    className={`status-pill status-pill--${client.status}`}
                  >
                    {statusLabel(client.status)}
                  </span>
                </div>

                {!editing ? (
                  <dl className="feature-company-workspace__facts">
                    <div>
                      <dt>Ім'я</dt>
                      <dd>{client.first_name || "—"}</dd>
                    </div>
                    <div>
                      <dt>Прізвище</dt>
                      <dd>{client.last_name || "—"}</dd>
                    </div>
                    {hasConfidentialKey(client, "phone") ? (
                      <div>
                        <dt>Телефон</dt>
                        <dd>{client.phone || "—"}</dd>
                      </div>
                    ) : null}
                    {hasConfidentialKey(client, "email") ? (
                      <div>
                        <dt>Email</dt>
                        <dd>{client.email || "—"}</dd>
                      </div>
                    ) : null}
                    <div>
                      <dt>Джерело</dt>
                      <dd>{client.source || "—"}</dd>
                    </div>
                    {hasConfidentialKey(client, "notes") ? (
                      <div>
                        <dt>Нотатки</dt>
                        <dd>{client.notes || "—"}</dd>
                      </div>
                    ) : null}
                    <div>
                      <dt>Автор</dt>
                      <dd>{userDisplayName(client.created_by) || "—"}</dd>
                    </div>
                  </dl>
                ) : (
                  <form className="feature-company-clients__inline-form" onSubmit={handleSave}>
                    <Field id="edit-first-name" label="Ім'я" error={coreFieldErrors.first_name}>
                      <TextInput
                        value={form.first_name}
                        onChange={setFormField("first_name")}
                      />
                    </Field>
                    <Field id="edit-last-name" label="Прізвище" error={coreFieldErrors.last_name}>
                      <TextInput
                        value={form.last_name}
                        onChange={setFormField("last_name")}
                      />
                    </Field>
                    {hasConfidentialKey(client, "phone") ? (
                      <Field id="edit-phone" label="Телефон" error={coreFieldErrors.phone}>
                        <TextInput
                          value={form.phone || ""}
                          onChange={setFormField("phone")}
                        />
                      </Field>
                    ) : null}
                    {hasConfidentialKey(client, "email") ? (
                      <Field id="edit-email" label="Email" error={coreFieldErrors.email}>
                        <TextInput
                          value={form.email || ""}
                          onChange={setFormField("email")}
                        />
                      </Field>
                    ) : null}
                    <Field id="edit-type" label="Тип" error={coreFieldErrors.type}>
                      <Select
                        value={form.type}
                        onChange={setFormField("type")}
                        options={TYPE_OPTIONS}
                      />
                    </Field>
                    <Field id="edit-status" label="Статус" error={coreFieldErrors.status}>
                      <Select
                        value={form.status}
                        onChange={setFormField("status")}
                        options={STATUS_OPTIONS}
                      />
                    </Field>
                    <Field id="edit-source" label="Джерело" error={coreFieldErrors.source}>
                      <TextInput
                        value={form.source}
                        onChange={setFormField("source")}
                      />
                    </Field>
                    {hasConfidentialKey(client, "notes") ? (
                      <Field id="edit-notes" label="Нотатки" error={coreFieldErrors.notes}>
                        <Textarea
                          value={form.notes || ""}
                          onChange={setFormField("notes")}
                          rows={3}
                        />
                      </Field>
                    ) : null}
                    {coreFieldErrors._form ? (
                      <InlineError>{coreFieldErrors._form}</InlineError>
                    ) : null}
                    <div className="feature-company-workspace__inline-actions">
                      <button type="submit" className="ui-form__button ui-form__button--primary">
                        Зберегти
                      </button>
                      <button
                        type="button"
                        className="action-btn action-btn--ghost"
                        onClick={() => {
                          setForm(emptyFormFromClient(client));
                          setCoreFieldErrors({});
                          setEditing(false);
                        }}
                      >
                        Скасувати
                      </button>
                    </div>
                  </form>
                )}

                <div className="feature-company-workspace__inline-actions">
                  {canEdit && !editing ? (
                    <button
                      type="button"
                      className="action-btn"
                      onClick={() => {
                        setCoreFieldErrors({});
                        setEditing(true);
                      }}
                    >
                      Редагувати
                    </button>
                  ) : null}
                  {canEdit ? (
                    <button
                      type="button"
                      className="action-btn action-btn--danger"
                      onClick={handleArchive}
                    >
                      Архівувати
                    </button>
                  ) : null}
                </div>
              </section>

              {showNeedSection ? (
                <section className="feature-company-workspace__card">
                  <h2>Потреба</h2>
                  {!needEditing ? (
                    <>
                      {needRows.length > 0 ? (
                        <dl className="feature-company-workspace__facts">
                          {needRows.map((row) => (
                            <div key={row.label}>
                              <dt>{row.label}</dt>
                              <dd>{row.value}</dd>
                            </div>
                          ))}
                        </dl>
                      ) : (
                        <p>Потребу ще не зазначено.</p>
                      )}
                      <div className="feature-company-workspace__inline-actions">
                        {showAddNeed ? (
                          <button
                            type="button"
                            className="action-btn"
                            onClick={openNeedEditor}
                          >
                            Додати потребу
                          </button>
                        ) : null}
                        {showEditNeed ? (
                          <button
                            type="button"
                            className="action-btn"
                            onClick={openNeedEditor}
                          >
                            Редагувати потребу
                          </button>
                        ) : null}
                      </div>
                    </>
                  ) : (
                    <form className="feature-company-clients__inline-form" onSubmit={handleNeedSave}>
                      <Field
                        id="need-deal-type"
                        label="Тип угоди"
                        error={needFieldErrors.deal_type}
                      >
                        <Select
                          value={needDraft.deal_type}
                          onChange={setNeedField("deal_type")}
                          options={DEAL_TYPE_OPTIONS}
                        />
                      </Field>
                      <Field
                        id="need-property-type"
                        label="Тип нерухомості"
                        error={needFieldErrors.property_type}
                      >
                        <Select
                          value={needDraft.property_type}
                          onChange={setNeedField("property_type")}
                          options={PROPERTY_TYPE_OPTIONS}
                        />
                      </Field>
                      <Field
                        id="need-min-price"
                        label="Бюджет від"
                        error={needFieldErrors.min_price}
                      >
                        <TextInput
                          value={needDraft.min_price}
                          onChange={setNeedField("min_price")}
                        />
                      </Field>
                      <Field
                        id="need-max-price"
                        label="Бюджет до"
                        error={needFieldErrors.max_price}
                      >
                        <TextInput
                          value={needDraft.max_price}
                          onChange={setNeedField("max_price")}
                        />
                      </Field>
                      <Field
                        id="need-min-area"
                        label="Площа від"
                        error={needFieldErrors.min_area}
                      >
                        <TextInput
                          value={needDraft.min_area}
                          onChange={setNeedField("min_area")}
                        />
                      </Field>
                      <Field
                        id="need-max-area"
                        label="Площа до"
                        error={needFieldErrors.max_area}
                      >
                        <TextInput
                          value={needDraft.max_area}
                          onChange={setNeedField("max_area")}
                        />
                      </Field>
                      <Field
                        id="need-rooms-min"
                        label="Кімнат від"
                        error={needFieldErrors.rooms_min}
                      >
                        <TextInput
                          value={needDraft.rooms_min}
                          onChange={setNeedField("rooms_min")}
                        />
                      </Field>
                      <Field
                        id="need-rooms-max"
                        label="Кімнат до"
                        error={needFieldErrors.rooms_max}
                      >
                        <TextInput
                          value={needDraft.rooms_max}
                          onChange={setNeedField("rooms_max")}
                        />
                      </Field>
                      <Field
                        id="need-city"
                        label="Місто / населений пункт"
                        error={needFieldErrors.city}
                      >
                        <TextInput
                          value={needDraft.city}
                          onChange={setNeedField("city")}
                        />
                      </Field>
                      <Field
                        id="need-district"
                        label="Район"
                        error={needFieldErrors.district}
                      >
                        <TextInput
                          value={needDraft.district}
                          onChange={setNeedField("district")}
                        />
                      </Field>
                      {needFieldErrors._form ? (
                        <InlineError>{needFieldErrors._form}</InlineError>
                      ) : null}
                      <div className="feature-company-workspace__inline-actions">
                        <button type="submit" className="ui-form__button ui-form__button--primary">
                          Зберегти
                        </button>
                        <button
                          type="button"
                          className="action-btn action-btn--ghost"
                          onClick={cancelNeedEditor}
                        >
                          Скасувати
                        </button>
                      </div>
                    </form>
                  )}
                </section>
              ) : null}

              {showContactsSection ? (
                <section className="feature-company-workspace__card">
                  <h2>Додаткові контакти</h2>
                  {contacts.length === 0 && contactMode == null ? (
                    <p>Додаткових контактів немає.</p>
                  ) : (
                    <ul className="feature-company-workspace__list">
                      {contacts.map((c) => (
                        <li key={c.id}>
                          <div>
                            {contactTypeLabel(c.type)}: {c.value}
                            {c.is_primary ? " · основний" : ""}
                          </div>
                          {canMutateConfidential &&
                          !(
                            contactMode &&
                            contactMode !== "add" &&
                            contactMode.id === c.id
                          ) ? (
                            <div className="feature-company-workspace__inline-actions">
                              <button
                                type="button"
                                className="action-btn action-btn--ghost"
                                onClick={() => openContactEdit(c)}
                              >
                                Редагувати
                              </button>
                              <button
                                type="button"
                                className="action-btn action-btn--danger"
                                onClick={() => handleContactDelete(c.id)}
                              >
                                Видалити
                              </button>
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}

                  {contactMode != null ? (
                    <form className="feature-company-clients__inline-form" onSubmit={handleContactSave}>
                      <Field
                        id="contact-type"
                        label="Тип"
                        error={contactFieldErrors.type}
                      >
                        <Select
                          value={contactDraft.type}
                          onChange={setContactField("type")}
                          options={CONTACT_TYPE_OPTIONS}
                        />
                      </Field>
                      <Field
                        id="contact-value"
                        label="Значення"
                        error={contactFieldErrors.value}
                      >
                        <TextInput
                          value={contactDraft.value}
                          onChange={setContactField("value")}
                        />
                      </Field>
                      <Checkbox
                        id="contact-is-primary"
                        label="Основний"
                        checked={Boolean(contactDraft.is_primary)}
                        onChange={setContactField("is_primary")}
                        error={contactFieldErrors.is_primary}
                      />
                      {contactFieldErrors._form ? (
                        <InlineError>{contactFieldErrors._form}</InlineError>
                      ) : null}
                      <div className="feature-company-workspace__inline-actions">
                        <button type="submit" className="ui-form__button ui-form__button--primary">
                          Зберегти
                        </button>
                        <button
                          type="button"
                          className="action-btn action-btn--ghost"
                          onClick={cancelContactEditor}
                        >
                          Скасувати
                        </button>
                      </div>
                    </form>
                  ) : null}

                  {canMutateConfidential && contactMode == null ? (
                    <div className="feature-company-workspace__inline-actions">
                      <button
                        type="button"
                        className="action-btn"
                        onClick={openContactAdd}
                      >
                        Додати
                      </button>
                    </div>
                  ) : null}
                </section>
              ) : null}

              {showCommunicationsSection ? (
                <section className="feature-company-workspace__card">
                  <h2>Комунікації</h2>
                  {communicationsState === "loading" ? (
                    <div className="feature-company-workspace__state">
                      <Spinner />
                    </div>
                  ) : null}
                  {communicationsState === "error" ? (
                    <p>Не вдалося завантажити комунікації.</p>
                  ) : null}
                  {communicationsState === "ready" ||
                  communicationsState === "idle" ? (
                    communications.length === 0 && !commOpen ? (
                      <p>Комунікацій ще немає.</p>
                    ) : (
                      <ul className="feature-company-workspace__list">
                        {communications.map((row) => (
                          <li key={row.id}>
                            <div>
                              {communicationTypeLabel(row.type)} ·{" "}
                              {communicationDirectionLabel(row.direction)}
                            </div>
                            <div>{row.content || "—"}</div>
                            <div>
                              {userDisplayName(row.created_by)} ·{" "}
                              {formatCommunicationTimestamp(row.created_at)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )
                  ) : null}

                  {commOpen ? (
                    <form className="feature-company-clients__inline-form" onSubmit={handleCommSave}>
                      <Field
                        id="comm-type"
                        label="Тип"
                        error={commFieldErrors.type}
                      >
                        <Select
                          value={commDraft.type}
                          onChange={setCommField("type")}
                          options={COMMUNICATION_TYPE_OPTIONS}
                        />
                      </Field>
                      <Field
                        id="comm-direction"
                        label="Напрямок"
                        error={commFieldErrors.direction}
                      >
                        <Select
                          value={commDraft.direction}
                          onChange={setCommField("direction")}
                          options={COMMUNICATION_DIRECTION_OPTIONS}
                        />
                      </Field>
                      <Field
                        id="comm-content"
                        label="Зміст"
                        error={commFieldErrors.content}
                      >
                        <Textarea
                          value={commDraft.content}
                          onChange={setCommField("content")}
                          rows={3}
                        />
                      </Field>
                      {commFieldErrors._form ? (
                        <InlineError>{commFieldErrors._form}</InlineError>
                      ) : null}
                      <div className="feature-company-workspace__inline-actions">
                        <button type="submit" className="ui-form__button ui-form__button--primary">
                          Зберегти
                        </button>
                        <button
                          type="button"
                          className="action-btn action-btn--ghost"
                          onClick={cancelCommEditor}
                        >
                          Скасувати
                        </button>
                      </div>
                    </form>
                  ) : null}

                  {canMutateConfidential && !commOpen ? (
                    <div className="feature-company-workspace__inline-actions">
                      <button
                        type="button"
                        className="action-btn"
                        onClick={openCommEditor}
                      >
                        Додати комунікацію
                      </button>
                    </div>
                  ) : null}
                </section>
              ) : null}
            </div>

            <aside className="cd-sidebar">
              {canManage ? (
                <div className="feature-company-clients__access">
                <p className="feature-company-clients__access-kicker">
                  Керування доступом
                </p>
                <section className="feature-company-workspace__card">
                  <h2>Відповідальний</h2>
                  <Field id="assign-user" label="Учасник">
                    <Select
                      value={assignUserId}
                      onChange={(e) => setAssignUserId(e.target.value)}
                      options={assigneeOptions}
                    />
                  </Field>
                  <div className="feature-company-workspace__inline-actions">
                    <button
                      type="button"
                      className="ui-form__button ui-form__button--primary"
                      onClick={handleAssign}
                    >
                      Зберегти призначення
                    </button>
                    <button
                      type="button"
                      className="action-btn action-btn--ghost"
                      onClick={handleClearResponsible}
                    >
                      Зняти
                    </button>
                  </div>
                </section>

                <section className="feature-company-workspace__card">
                  <h2>Доступ колег</h2>
                  {grants.length === 0 ? (
                    <p>Активних доступів немає.</p>
                  ) : (
                    <ul className="feature-company-workspace__list">
                      {grants
                        .filter((g) => !g.revoked_at)
                        .map((g) => (
                          <li key={g.id}>
                            <div>{userDisplayName(g.user)}</div>
                            <div>
                              Редагування: {g.can_edit ? "так" : "ні"} ·
                              Конфіденційні дані:{" "}
                              {g.include_confidential ? "так" : "ні"}
                              {g.expires_at
                                ? ` · до ${g.expires_at}`
                                : ""}
                            </div>
                            <button
                              type="button"
                              className="action-btn action-btn--ghost"
                              onClick={() => handleRevokeGrant(g.id)}
                            >
                              Відкликати
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                  <form className="feature-company-clients__inline-form" onSubmit={handleUpsertGrant}>
                    <Field id="grant-user" label="Учасник">
                      <Select
                        value={grantDraft.user_id}
                        onChange={(e) =>
                          setGrantDraft((p) => ({
                            ...p,
                            user_id: e.target.value,
                          }))
                        }
                        options={grantUserOptions}
                      />
                    </Field>
                    <Checkbox
                      id="grant-can-edit"
                      label="Дозволити редагування"
                      checked={Boolean(grantDraft.can_edit)}
                      onChange={(e) =>
                        setGrantDraft((p) => ({
                          ...p,
                          can_edit: e.target.checked,
                        }))
                      }
                    />
                    <Checkbox
                      id="grant-include-confidential"
                      label="Показувати конфіденційні дані"
                      checked={Boolean(grantDraft.include_confidential)}
                      onChange={(e) =>
                        setGrantDraft((p) => ({
                          ...p,
                          include_confidential: e.target.checked,
                        }))
                      }
                    />
                    <Field
                      id="grant-expires"
                      label="Діє до (необов'язково)"
                    >
                      <TextInput
                        type="datetime-local"
                        value={grantDraft.expires_at}
                        onChange={(e) =>
                          setGrantDraft((p) => ({
                            ...p,
                            expires_at: e.target.value,
                          }))
                        }
                      />
                    </Field>
                    <button type="submit" className="ui-form__button ui-form__button--primary">
                      Зберегти доступ
                    </button>
                  </form>
                </section>
                </div>
              ) : null}
            </aside>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default CompanyClientDetailContent;
